import { useState, type ReactNode } from 'react';
import { CollapsibleCard } from '../primitives/CollapsibleCard';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, FONT_DISPLAY, FONT_MONO, TEXT_PRIMARY, TEXT_SECONDARY } from '../../tokens';

/** Header for a story beat: a mono label above a display-face title. */
function StoryTitle({ label, title }: { label: string; title: string }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: '0.65rem',
          color: ACCENT,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: FONT_DISPLAY,
          fontSize: 'clamp(1.3rem,2vw,1.6rem)',
          fontWeight: 400,
          lineHeight: 1.1,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: TEXT_PRIMARY,
          fontStyle: 'normal',
        }}
      >
        {title}
      </span>
    </span>
  );
}

interface StoryBeat {
  label: string;
  title: string;
  body: ReactNode;
}

const STORY_BEATS: readonly StoryBeat[] = [
  {
    label: 'F This Event #1',
    title: "The Vet Bill I Couldn't Pay",
    body: (
      <>
        <p>
          I was 18, working a job that paid me twenty bucks an hour AUD while the adult minimum wage was
          twenty-six, so I was already below minimum wage as a legal adult and didn&rsquo;t have the
          standing to do anything about it. Then my cat died, and the veterinary bill at the end of her
          life was bigger than anything I had in my account, and I had to ask for the money, which is the
          moment I realized that being broke isn&rsquo;t the worst part of being broke, the worst part is
          the position it puts you in when something you actually care about needs help and you
          don&rsquo;t have the resources to be the person who handles it.
        </p>
        <p style={{ marginTop: 16 }}>
          The lesson wasn&rsquo;t &ldquo;don&rsquo;t adopt pets.&rdquo; The lesson was: I never want to be
          in a position again where my financial situation forces me to ask for help on something I
          should have been able to handle myself. That was the first F This Event, and it was the moment
          I started looking at online business, not because I wanted to be an entrepreneur as an
          aesthetic, but because I needed a path to consistent cash flow that didn&rsquo;t depend on
          someone else deciding what I was worth.
        </p>
      </>
    ),
  },
  {
    label: 'F This Event #2',
    title: 'The Defence Contracting Rejection',
    body: (
      <>
        <p>
          I needed cash flow to fund the build, so I went after a contracting role through a recruiter for
          some defence-adjacent government work. I went in thinking this was going to be the easiest job
          I&rsquo;d ever interviewed for, I had a year of AI experience that almost nobody walking into
          that room had, I&rsquo;d built systems that compressed weeks of admin work into hours, and I was
          confident the value I could bring was obvious. And then the other candidates walked in, and
          without being harsh about it, a lot of them looked like the bottom of the barrel, guys who
          hadn&rsquo;t done anything meaningful with the last few years of their life, and <em>they</em>{' '}
          got hired, and I didn&rsquo;t.
        </p>
        <p style={{ marginTop: 16 }}>
          The lesson wasn&rsquo;t that the recruiters were wrong, the lesson was that in certain rooms,
          government, defence, anything where compliance and predictability outweigh capability, the AI
          experience I was bringing was actually a risk factor, not an asset, because it represented
          change and change is the thing that gets de-prioritized when stability is what&rsquo;s being
          optimized for. That was F This Event number two, and what crystallized in that moment was:
          I&rsquo;m not optimized for those rooms, and I shouldn&rsquo;t be trying to fit into them, the
          value I can give to people in the rooms I <em>am</em> optimized for is exponentially higher than
          the value I&rsquo;d ever produce inside a contracting seat I had to mask my edge to keep.
        </p>
        <p style={{ marginTop: 16 }}>So I stopped trying. And that&rsquo;s when this business became the only thing.</p>
      </>
    ),
  },
];

export function StorySection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const proseStyle = {
    color: TEXT_SECONDARY,
    fontSize: 'clamp(1rem,1.15vw,1.08rem)',
    lineHeight: 1.85,
  } as const;

  return (
    <section
      style={{
        padding: isMobile ? '60px 20px' : '100px 64px',
        background: 'rgba(6,6,6,0.88)',
        borderTop: `1px solid ${BORDER}`,
      }}
    >
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ marginBottom: 48 }}>
            <SectionLabel number="01">The Story</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>
              Two moments that made this business inevitable.
            </SectionH2>
          </div>
        </FadeUp>

        <FadeUp delay={80}>
          <div style={{ ...proseStyle, marginBottom: 40 }}>
            <p style={{ marginBottom: 24 }}>
              There&rsquo;s a concept from MJ DeMarco&rsquo;s book{' '}
              <em style={{ color: TEXT_PRIMARY }}>
                Unscripted: Life, Liberty and the Pursuit of Entrepreneurship
              </em>{' '}
              called the <strong style={{ color: TEXT_PRIMARY }}>&ldquo;F This Event&rdquo;</strong>, the
              moment where the gap between where you are and where you can&rsquo;t stand to be anymore
              becomes so painfully obvious that doing nothing is the only thing that hurts more than
              changing. It&rsquo;s not a decision you make at a desk with a notepad, it&rsquo;s a thing
              that happens <em>to</em> you, and if you don&rsquo;t act on it, you stay stuck and the misery
              compounds.
            </p>
            <p style={{ marginBottom: 24 }}>
              I&rsquo;ve had two of these so far, and the second one is the reason this business exists.
            </p>
          </div>
        </FadeUp>

        {STORY_BEATS.map((beat, i) => (
          <FadeUp key={beat.label} delay={120 + i * 40}>
            <div style={{ marginBottom: 32 }}>
              <CollapsibleCard
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                title={<StoryTitle label={beat.label} title={beat.title} />}
              >
                {beat.body}
              </CollapsibleCard>
            </div>
          </FadeUp>
        ))}

        <FadeUp delay={200}>
          <div style={proseStyle}>
            <p style={{ marginBottom: 24 }}>
              I went all in on AI starting May 2025, and what happened over the next twelve months
              wasn&rsquo;t linear, it was exponential. Not just my own productivity, but the value of the
              workflows I was building, the same agent that took me a weekend to build in month one is now
              doing work that would have taken a senior operator a full quarter, and the gap between where
              someone who&rsquo;s been using AI properly for a year sits and where someone who&rsquo;s just
              starting sits widens every single week the system is running. It&rsquo;s like a stock that
              just keeps rising, except the floor underneath you also keeps rising, so even a bad week
              still leaves you further ahead than you were a month ago.
            </p>
            <p>
              That&rsquo;s when I realized two things at once: first, that AI used properly is genuinely a
              superpower and not in a marketing sense, in the sense that the math of compounding actually
              plays out in your favor for once. And second, that the type of work I was doing was so
              unsaturated that almost nobody was doing it correctly, and the people who were doing it
              correctly were charging enterprise rates for it, which left a massive gap for someone willing
              to install it properly at a price point that small and mid-sized operators could actually
              access.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
